import { ClientUser, ClientUserStatuses } from '@zerops/models/client-user';
import { User } from '@zerops/models/user';

/**
 * True when the client-user list for the active client appears to no longer
 * include the active user.
 *
 * A SUSPICION only. It reads WS/list-subscription entity data, which the zef
 * entity merger assembles from several endpoints by deep-merging with no
 * version ordering — so a cached row can lag, or carry fields from more than
 * one response. A caller MUST confirm via `confirmsRemoval` against a direct
 * `/user/info` read before acting on it.
 */
export function suspectsRemoval(
  clientUsers: ClientUser[] | undefined,
  activeClientUser: Pick<ClientUser, 'clientId' | 'userId'> | undefined
): boolean {
  if (!clientUsers?.length || !activeClientUser) return false;

  const { clientId, userId } = activeClientUser;

  const hasRowForActiveClient = clientUsers.some((cu) => cu.clientId === clientId);
  if (!hasRowForActiveClient) return false;

  // Match on the `userId` scalar, never the nested `user.id` copy of the same
  // fact: both operands then read the SAME field of the same cached row, so a
  // row whose inline `user` object is out of step with its own scalar can't
  // fake a removal.
  return !clientUsers.some((cu) => cu.clientId === clientId && cu.userId === userId);
}

export interface RemovalConfirmationContext {
  activeUserId: string;
  activeClientId: string;
}

/**
 * True only when a well-formed `/user/info` payload — read directly, not
 * from cached/WS entity data — for the SAME user carries no ACTIVE
 * membership for the active client.
 *
 * Strict fail-safe: a malformed or ambiguous shape (missing user id, absent
 * or non-array `clientUserList`, a membership row missing its own id,
 * clientId or status, or a payload belonging to a different user) always
 * returns false. Never wipe on bad data.
 */
export function confirmsRemoval(
  userInfo: User | null | undefined,
  ctx: RemovalConfirmationContext | undefined
): boolean {
  if (!userInfo || !ctx || !ctx.activeUserId || !ctx.activeClientId) return false;
  if (!userInfo.id || userInfo.id !== ctx.activeUserId) return false;

  const list = userInfo.clientUserList;
  if (!Array.isArray(list)) return false;
  if (list.some((cu) => !cu || !cu.id || !cu.clientId || !cu.status)) return false;

  const hasActiveMembershipForClient = list.some(
    (cu) => cu.clientId === ctx.activeClientId && cu.status === ClientUserStatuses.active
  );

  return !hasActiveMembershipForClient;
}
